import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import type { components } from '../generated/api';
import { trackUserJourney, setUserContext } from '../utils/datadog';
import { GRACE_PAWPPER } from '../utils/constants';
import { getRumUser } from '../utils/users';
import './EnrollPage.css';

type SSNExistsResponse = components['schemas']['SSNExistsResponse'];
type CreateAccountRequest = components['schemas']['CreateAccountRequest'];
type CreateAccountResponse = components['schemas']['CreateAccountResponse'];
type CredentialsRequest = components['schemas']['CredentialsRequest'];
type DepositRequest = components['schemas']['DepositRequest'];
type SSNValidationRequest = components['schemas']['SSNValidationRequest'];
type SSNValidationResponse = components['schemas']['SSNValidationResponse'];
type IdentityVerificationRequest = components['schemas']['IdentityVerificationRequest'];
type IdentityVerificationResponse = components['schemas']['IdentityVerificationResponse'];
type WelcomeEmailRequest = components['schemas']['WelcomeEmailRequest'];
type WelcomeEmailResponse = components['schemas']['WelcomeEmailResponse'];
type OnlineBankingCredentialsResponse = components['schemas']['OnlineBankingCredentialsResponse'];
type DepositResponse = components['schemas']['DepositResponse'];
type AgeValidationRequest = components['schemas']['AgeValidationRequest'];
type AgeValidationResponse = components['schemas']['AgeValidationResponse'];
type FraudCheckRequest = components['schemas']['FraudCheckRequest'];
type FraudCheckResponse = components['schemas']['FraudCheckResponse'];

const EnrollPage: React.FC = () => {
  // Get RUM user for Product Analytics
  const username = getRumUser();
  const email = `${username}@bitsburg.io`;

  // Extract first and last name from username (e.g., "bella.barker" or "bella.barker42")
  const usernameWithoutNumbers = username.replace(/\d+$/, ''); // Remove trailing numbers
  const [firstName = GRACE_PAWPPER.FIRST_NAME, lastName = GRACE_PAWPPER.LAST_NAME] = usernameWithoutNumbers.split('.');
  const capitalizedFirstName = firstName.charAt(0).toUpperCase() + firstName.slice(1);
  const capitalizedLastName = lastName.charAt(0).toUpperCase() + lastName.slice(1);

  React.useEffect(() => {
    document.title = 'Bitsburg Bank - Enroll';

    // Track user journey step
    trackUserJourney('enroll_page_visited', {
      previous_page: document.referrer || 'direct',
      timestamp: new Date().toISOString(),
    });
  }, []);

  const [formData, setFormData] = useState({
    accountType: 'checking',
    firstName: capitalizedFirstName,
    lastName: capitalizedLastName,
    ssn: '123-45-6789',
    dateOfBirth: '1906-12-09',
    email: email,
    phone: '123-867-5309',
    favoriteDogBreed: 'labrador',
    agreeTerms: true,
    marketingConsent: false,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => ({
        ...prev,
        [name]: checked,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const formatSSN = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length >= 3 && numbers.length <= 5) {
      return numbers.slice(0, 3) + '-' + numbers.slice(3);
    } else if (numbers.length > 5) {
      return numbers.slice(0, 3) + '-' + numbers.slice(3, 5) + '-' + numbers.slice(5, 9);
    }
    return numbers;
  };

  const formatPhone = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length >= 6) {
      return '(' + numbers.slice(0, 3) + ') ' + numbers.slice(3, 6) + '-' + numbers.slice(6, 10);
    } else if (numbers.length >= 3) {
      return '(' + numbers.slice(0, 3) + ') ' + numbers.slice(3);
    }
    return numbers;
  };

  const handleSSNChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatSSN(e.target.value);
    setFormData((prev) => ({ ...prev, ssn: formatted }));
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhone(e.target.value);
    setFormData((prev) => ({ ...prev, phone: formatted }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setShowErrorMessage(false);

    // Basic form validation
    if (
      !formData.accountType ||
      !formData.firstName ||
      !formData.lastName ||
      !formData.ssn ||
      !formData.dateOfBirth ||
      !formData.email ||
      !formData.phone ||
      !formData.favoriteDogBreed ||
      !formData.agreeTerms
    ) {
      setErrorMessage('Please fill in all required fields and accept the terms and conditions.');
      setShowErrorMessage(true);
      setIsLoading(false);
      return;
    }

    // Validate SSN format
    if (!/^\d{3}-\d{2}-\d{4}$/.test(formData.ssn)) {
      setErrorMessage('Please enter a valid Social Security Number in XXX-XX-XXXX format.');
      setShowErrorMessage(true);
      setIsLoading(false);
      return;
    }

    // Validate age via Security API
    const ageValidationRequest: AgeValidationRequest = {
      dateOfBirth: new Date(formData.dateOfBirth).toISOString(),
    };

    const ageValidationResponse = await fetch('/api/security/age', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(ageValidationRequest),
    });

    if (!ageValidationResponse.ok) {
      setErrorMessage('Unable to verify age. Please try again later.');
      setShowErrorMessage(true);
      setIsLoading(false);
      return;
    }

    const ageResult = (await ageValidationResponse.json()) as AgeValidationResponse;

    if (!ageResult.valid) {
      setErrorMessage(ageResult.message || 'You must be at least 18 years old to open an account.');
      setShowErrorMessage(true);
      setIsLoading(false);
      return;
    }

    try {
      // Check if a user with this SSN already exists
      const existsResponse = await fetch(`/api/security/ssn/exists?ssn=${encodeURIComponent(formData.ssn)}`);

      if (!existsResponse.ok) {
        setErrorMessage('An error occurred during verification. Please try again later.');
        setShowErrorMessage(true);
        return;
      }

      const existsResult = (await existsResponse.json()) as SSNExistsResponse;

      if (existsResult.exists === true) {
        setErrorMessage('An account with this information already exists. Please contact customer service.');
        setShowErrorMessage(true);
        return;
      }

      // Validate SSN with Security API
      const ssnValidationRequest: SSNValidationRequest = {
        ssn: formData.ssn,
      };

      const ssnValidationResponse = await fetch('/api/security/validate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(ssnValidationRequest),
      });

      if (!ssnValidationResponse.ok) {
        setErrorMessage(
          'Unable to verify your identity. Please ensure your information is correct or contact customer service.'
        );
        setShowErrorMessage(true);
        return;
      }

      const ssnResult = (await ssnValidationResponse.json()) as SSNValidationResponse;

      if (ssnResult.valid !== true) {
        setErrorMessage(
          'Unable to verify your identity. Please ensure your information is correct or contact customer service.'
        );
        setShowErrorMessage(true);
        return;
      }

      // Perform identity verification
      const identityVerificationRequest: IdentityVerificationRequest = {
        ssn: formData.ssn,
        firstName: formData.firstName,
        lastName: formData.lastName,
        dateOfBirth: new Date(formData.dateOfBirth).toISOString(),
      };

      const identityResponse = await fetch('/api/security/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(identityVerificationRequest),
      });

      if (!identityResponse.ok) {
        setErrorMessage('Additional verification is required. Our team will contact you within 24 hours.');
        setShowErrorMessage(true);
        return;
      }

      const identity = (await identityResponse.json()) as IdentityVerificationResponse;

      if (identity.verified !== true) {
        setErrorMessage('Additional verification is required. Our team will contact you within 24 hours.');
        setShowErrorMessage(true);
        return;
      }

      const identityScore = identity.identityScore || 0;
      if (identityScore < 20) {
        setErrorMessage('Additional verification is required. Our team will contact you within 24 hours.');
        setShowErrorMessage(true);
        return;
      }

      // Perform fraud check via Security API
      const fraudCheckRequest: FraudCheckRequest = {
        ssn: formData.ssn,
        email: formData.email,
        phone: formData.phone,
      };

      const fraudResponse = await fetch('/api/security/fraud', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(fraudCheckRequest),
      });

      if (!fraudResponse.ok) {
        setErrorMessage('Security verification failed. Please try again later.');
        setShowErrorMessage(true);
        return;
      }

      const fraudResult = (await fraudResponse.json()) as FraudCheckResponse;

      if (fraudResult.riskLevel === 'high' || (fraudResult.fraudScore && fraudResult.fraudScore > 70)) {
        setErrorMessage('Additional verification is required. Our team will contact you within 24 hours.');
        setShowErrorMessage(true);
        return;
      }

      // Create account via Account API
      const createAccountRequest: CreateAccountRequest = {
        accountType: formData.accountType,
        firstName: formData.firstName,
        lastName: formData.lastName,
        ssn: formData.ssn,
        dateOfBirth: formData.dateOfBirth,
        email: formData.email,
        phone: formData.phone,
      };

      const createAccountResponse = await fetch('/api/account/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(createAccountRequest),
      });

      if (!createAccountResponse.ok) {
        setErrorMessage('An error occurred during account creation. Please try again later.');
        setShowErrorMessage(true);
        return;
      }

      const accountResult = (await createAccountResponse.json()) as CreateAccountResponse;
      const accountNumber = accountResult.accountNumber || '';

      // Set up online banking credentials via Account API
      try {
        const credentialsRequest: CredentialsRequest = {
          accountNumber: accountNumber,
          email: formData.email,
        };

        const credentialsResponse = await fetch('/api/account/credentials', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(credentialsRequest),
        });

        if (credentialsResponse.ok) {
          const credentialsResult = (await credentialsResponse.json()) as OnlineBankingCredentialsResponse;
          window.DD_LOGS?.logger?.info('Online banking credentials setup', { message: credentialsResult.message });
        }
      } catch (error) {
        // Don't fail enrollment, continue without credentials setup
        window.DD_LOGS?.logger?.warn('Failed to set up online banking credentials', { error });
      }

      // Send welcome email via Email API
      try {
        const welcomeEmailRequest: WelcomeEmailRequest = {
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          accountType: formData.accountType,
          accountNumber: accountNumber,
        };

        const welcomeResponse = await fetch('/api/email/welcome', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(welcomeEmailRequest),
        });

        if (welcomeResponse.ok) {
          const welcomeResult = (await welcomeResponse.json()) as WelcomeEmailResponse;
          window.DD_LOGS?.logger?.info('Welcome email sent', { message: welcomeResult.message });
        }
      } catch (error) {
        // Don't fail the enrollment, just log the issue
        window.DD_LOGS?.logger?.warn('Failed to send welcome email', { error: error });
      }

      // Schedule initial deposit processing (only for checking or savings accounts)
      if (formData.accountType === 'checking' || formData.accountType === 'savings') {
        try {
          const depositRequest: DepositRequest = {
            accountNumber: accountNumber,
            amount: 100.0,
          };

          const depositResponse = await fetch('/api/account/deposit', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(depositRequest),
          });

          if (depositResponse.ok) {
            const depositResult = (await depositResponse.json()) as DepositResponse;
            window.DD_LOGS?.logger?.info('Initial deposit scheduled', { message: depositResult.message });
          }
        } catch (error) {
          // Don't fail enrollment, continue without deposit scheduling
          window.DD_LOGS?.logger?.warn('Failed to schedule initial deposit', { error: error });
        }
      }

      // Success - create consistent timestamp for journey tracking
      const currentTimestamp = new Date().toISOString();

      // Set user context once for the entire RUM session
      setUserContext(username, {
        enrollment_method: 'web',
        enrollment_timestamp: currentTimestamp,
        account_type: formData.accountType,
      });

      // Track enrollment completion journey step
      trackUserJourney('enrollment_completed', {
        account_type: formData.accountType,
        account_number: accountNumber,
        timestamp: currentTimestamp,
      });

      setShowSuccessMessage(true);
    } catch (error) {
      window.DD_LOGS?.logger?.error('Error during enrollment', { error: error });
      setErrorMessage('An unexpected error occurred. Please try again later.');
      setShowErrorMessage(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="py-5">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-7 col-md-9">
            <div className="bank-card bank-card-elevated bank-card-no-hover">
              <div className="bank-card-header">
                <h3 className="mb-0 fw-bold">
                  <i className="bi bi-person-plus me-2"></i>
                  Open Your Bitsburg Bank Account
                </h3>
                <p className="mb-0 mt-2 opacity-75">Start your banking journey with us today</p>
              </div>
              <div className="bank-card-body">
                {showSuccessMessage && (
                  <div className="alert bank-alert bank-alert-success mb-4">
                    <i className="bi bi-check-circle me-2"></i>
                    Thank you for your application! We've received your information and will review it within 1-2
                    business days. You'll receive an email with next steps once your application is processed.
                  </div>
                )}

                {showErrorMessage && (
                  <div className="alert bank-alert bank-alert-danger mb-4">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    {errorMessage}
                  </div>
                )}

                {!showSuccessMessage && (
                  <form onSubmit={handleSubmit}>
                    {/* Account Type Selection */}
                    <div className="mb-4">
                      <label htmlFor="accountType" className="bank-form-label">
                        <i className="bi bi-bank2 me-2 text-datadog-purple"></i>
                        Choose Your Account Type <span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-select bank-form-control account-type-select"
                        id="accountType"
                        name="accountType"
                        value={formData.accountType}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Choose the account that fits your needs</option>
                        <option value="checking">🏦 Checking - Free everyday banking with mobile access</option>
                        <option value="savings">💰 Savings - Grow your money with competitive rates</option>
                        <option value="investment">📈 Investment - Start building your portfolio</option>
                      </select>
                    </div>

                    <div className="row">
                      {/* Personal Information */}
                      <div className="col-md-6 mb-4">
                        <label htmlFor="firstName" className="bank-form-label">
                          First Name <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control bank-form-control"
                          id="firstName"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleInputChange}
                          placeholder="Enter your first name"
                          required
                        />
                      </div>
                      <div className="col-md-6 mb-4">
                        <label htmlFor="lastName" className="bank-form-label">
                          Last Name <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control bank-form-control"
                          id="lastName"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleInputChange}
                          placeholder="Enter your last name"
                          required
                        />
                      </div>
                    </div>

                    <div className="mb-4">
                      <label htmlFor="ssn" className="bank-form-label">
                        Social Security Number <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control bank-form-control"
                        id="ssn"
                        name="ssn"
                        value={formData.ssn}
                        onChange={handleSSNChange}
                        maxLength={11}
                        pattern="[0-9]{3}-[0-9]{2}-[0-9]{4}"
                        readOnly
                        required
                      />
                    </div>

                    <div className="mb-4">
                      <label htmlFor="dateOfBirth" className="bank-form-label">
                        Date of Birth <span className="text-danger">*</span>
                      </label>
                      <input
                        type="date"
                        className="form-control bank-form-control"
                        id="dateOfBirth"
                        name="dateOfBirth"
                        value={formData.dateOfBirth}
                        onChange={handleInputChange}
                        required
                      />
                    </div>

                    {/* Contact Information */}
                    <div className="mb-4">
                      <label htmlFor="email" className="bank-form-label">
                        Email Address <span className="text-danger">*</span>
                      </label>
                      <input
                        type="email"
                        className="form-control bank-form-control"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="Enter your email address"
                        required
                      />
                    </div>

                    <div className="mb-4">
                      <label htmlFor="phone" className="bank-form-label">
                        Phone Number <span className="text-danger">*</span>
                      </label>
                      <input
                        type="tel"
                        className="form-control bank-form-control"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handlePhoneChange}
                        placeholder="(XXX) XXX-XXXX"
                        required
                      />
                    </div>

                    <div className="mb-4">
                      <label htmlFor="favoriteDogBreed" className="bank-form-label">
                        Favorite Dog Breed <span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-select bank-form-control"
                        id="favoriteDogBreed"
                        name="favoriteDogBreed"
                        value={formData.favoriteDogBreed}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Choose your favorite dog breed</option>
                        <option value="golden-retriever">🐕 Golden Retriever</option>
                        <option value="labrador">🦮 Labrador Retriever</option>
                        <option value="german-shepherd">🐺 German Shepherd</option>
                        <option value="bulldog">🐶 Bulldog</option>
                        <option value="poodle">🐩 Poodle</option>
                        <option value="beagle">🐕‍🦺 Beagle</option>
                        <option value="rottweiler">🐕 Rottweiler</option>
                        <option value="yorkshire-terrier">🐕 Yorkshire Terrier</option>
                        <option value="dachshund">🌭 Dachshund</option>
                        <option value="siberian-husky">🐺 Siberian Husky</option>
                        <option value="shih-tzu">🐕 Shih Tzu</option>
                        <option value="border-collie">🐕‍🦺 Border Collie</option>
                        <option value="boxer">🥊 Boxer</option>
                        <option value="great-dane">🐕 Great Dane</option>
                        <option value="chihuahua">🐕 Chihuahua</option>
                        <option value="shiba-inu">🐕 Shiba Inu</option>
                        <option value="corgi">🐕 Corgi</option>
                        <option value="other">🐕 Other</option>
                        <option value="cats">🐱 I'm actually a cat person</option>
                        <option value="other-pets">🐠 I prefer other pets (fish, birds, reptiles, etc.)</option>
                      </select>
                    </div>

                    {/* Terms and Conditions */}
                    <div className="mb-4">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="agreeTerms"
                          name="agreeTerms"
                          checked={formData.agreeTerms}
                          onChange={handleInputChange}
                          required
                        />
                        <label className="form-check-label" htmlFor="agreeTerms">
                          I agree to the{' '}
                          <a href="#" className="text-datadog-purple">
                            Terms and Conditions
                          </a>{' '}
                          and
                          <a href="#" className="text-datadog-purple">
                            {' '}
                            Privacy Policy
                          </a>{' '}
                          <span className="text-danger">*</span>
                        </label>
                      </div>
                    </div>

                    <div className="mb-4">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="marketingConsent"
                          name="marketingConsent"
                          checked={formData.marketingConsent}
                          onChange={handleInputChange}
                        />
                        <label className="form-check-label" htmlFor="marketingConsent">
                          I would like to receive promotional offers and updates via email
                        </label>
                      </div>
                    </div>

                    <div className="text-center">
                      <button
                        type="submit"
                        className="bank-btn-primary px-4"
                        disabled={isLoading}
                        id="enroll-submit-btn"
                      >
                        {!isLoading ? (
                          <span>
                            <i className="bi bi-check-circle me-2"></i>
                            <span>Submit Application</span>
                          </span>
                        ) : (
                          <span>
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                              aria-hidden="true"
                            ></span>
                            <span>Processing Application...</span>
                          </span>
                        )}
                      </button>
                    </div>
                  </form>
                )}

                <div className="text-center mt-4">
                  <Link to="/login" className="text-datadog-purple text-decoration-none">
                    <i className="bi bi-arrow-left me-1"></i>
                    Back to Login
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnrollPage;
